import {Link} from "react-router";

import {UnlinkCheckButton} from "@/components/ChecksModalButton/subComponents/UnlinkCheckButton.tsx";
import {Chip} from "@/components/chip/Chip.tsx";
import {LoadingContainer} from "@/components/placeholder/LoadingContainer.tsx";
import {QuickActionsBar} from "@/components/quickActionsBar/QuickActionsBar.tsx";
import {EmptyTable} from "@/components/table/subComponents/EmptyTable/EmptyTable.tsx";
import {LimitAndOffset} from "@/components/table/subComponents/Paginator/Paginator.tsx";
import {Table} from "@/components/table/Table.tsx";
import {ColumnSpec, Sorting} from "@/components/table/Table.types.ts";
import {ChecksQueryModelType} from "@/lib/queries/checks.ts";
import {CheckTypeDisplay, MonitoringServiceType, ServiceList} from "@/lib/types/checks.ts";
import {MaintenanceAuditEventCheck} from "@/lib/types/maintenance.ts";

import styles from "./ChecksModalTable.module.scss";

type AffectedCheckRow = {
  id: number;
  name: string;
  monitoring_service_type: MonitoringServiceType | null;
  is_deleted?: boolean;
};

interface LocationChecksTableProps {
  data?: ServiceList;
  auditChecks?: {items: MaintenanceAuditEventCheck[]; total: number};
  sorting?: Sorting;
  onSortingChange?: (sorting: Sorting) => void;
  pagination: LimitAndOffset;
  updatePagination: (laf: LimitAndOffset) => void;
  isLoading?: boolean;
  modelType?: ChecksQueryModelType;
  modelId?: number;
}

export const ChecksModalTable = ({
  data,
  auditChecks,
  sorting,
  onSortingChange,
  pagination,
  updatePagination,
  isLoading,
  modelType,
  modelId,
}: LocationChecksTableProps) => {
  if (auditChecks === undefined && data === undefined) {
    return <LoadingContainer />;
  }

  const rows: AffectedCheckRow[] = auditChecks ? auditChecks.items : (data?.services.items ?? []);
  const totalRows = auditChecks ? auditChecks.total : (data?.services.total ?? 0);

  const specs: ColumnSpec<AffectedCheckRow>[] = [
    {
      fieldName: "name",
      fieldTitle: "Check Name",
      enableSorting: true,
      meta: {
        headerClassName: styles.nameColumn,
      },
      accessor: (check: AffectedCheckRow) =>
        check.is_deleted ? (
          <span className="d-flex align-items-center gap-2">
            <span className="pulse-color-gray-700">{check.name}</span>
            <Chip variant="tag" size="tiny">
              Deleted
            </Chip>
          </span>
        ) : (
          <Link to={`/checks/${check.id}`} target={"_blank"}>
            {check.name}
          </Link>
        ),
    },
    {
      fieldName: "type",
      fieldTitle: "Check Type",
      meta: {
        headerClassName: styles.typeColumn,
      },
      accessor: (check: AffectedCheckRow) => (
        <>{check.monitoring_service_type ? CheckTypeDisplay[check.monitoring_service_type] : null}</>
      ),
    },
  ];

  if (modelType === "MaintenanceSchedule" && modelId) {
    specs.push({
      fieldName: "actions",
      fieldTitle: "",
      meta: {
        headerClassName: styles.actionColumn,
        columnClassName: styles.actionColumn,
      },
      accessor: (check: AffectedCheckRow) => (
        <QuickActionsBar
          id={`unlink-check-${check.id}`}
          actions={[<UnlinkCheckButton key="unlink" scheduleId={modelId} check={check} />]}
        />
      ),
    });
  }

  return (
    <Table
      rows={rows}
      sorting={sorting}
      onSortingChange={onSortingChange}
      isLoading={isLoading}
      specs={specs}
      initialState={{columnPinning: {right: ["actions"]}}}
      noDataComponent={<EmptyTable>No Checks found</EmptyTable>}
      pagination={pagination}
      updatePagination={updatePagination}
      totalRows={totalRows}
      paginationData={rows}
      fixedWidth={true}
      minWidth={"600px"}
    />
  );
};
